import { down, up } from 'styled-breakpoints'
import styled, { css, keyframes } from 'styled-components'

const updownAnimation = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

interface WingieSearchFormCardProps {
  isAffixed?: boolean
  isWingie?: boolean
  $variant?: string
}
export const WingieSearchFormCard = styled.div<WingieSearchFormCardProps>`
  border-radius: 8px;
  padding: 16px;
  padding-top: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  box-shadow: none;

  ${up('lg')} {
    width: 1120px;
    max-width: 100%;
    margin: 0 auto;
  }

  ${down('lg')} {
    max-width: 98%;
    margin: 0 auto;
  }

  ${down('md')} {
    border-radius: 30px 30px 0px 0px;
    background: var(--sky-extra-extra-light, #f8faf9);
    box-shadow: none;
    max-width: 100%;

    ${({ $variant }) =>
      $variant === 'B' &&
      css`
        padding-right: 16px;
        padding-left: 16px;
      `}
  }
`

export const SearchFormAffixWrapper = styled(WingieSearchFormCard)<{
  isAffixed: boolean
  isWingie: boolean
  market?: string
  language?: string
  $variant?: string
  $flightOnly?: boolean
  $tabCount?: number
}>`
  /* Enuygun: 0 — dış container zaten 14px 16px veriyor. Wingie: 8px 16px 16px. */
  padding: 0px;
  padding: ${(p) => p.isWingie && '8px 16px 16px'};
  will-change: transform, opacity;
  position: relative;
  z-index: 10;

  [dir='rtl'] & [data-testid='endesign-wingie-search-form-tab-list'] [role='tab'] > div {
    gap: 4px;
  }

  [dir='rtl'] & [data-testid='endesign-wingie-search-form-tab-list'] [role='tab'] > div > :first-child {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  ${up('md')} {
    /*
      Tab varken separated-panels 16px padding verir → wrapper 0.
      Otel tab yokken ($flightOnly) Tab kalkıyor; padding burada verilmezse
      up('md') media query $flightOnly padding'ini ezer ve kart dibine yapışır.
    */
    padding: ${({ $flightOnly }) => ($flightOnly ? '16px' : '0')};

    /*
      Traveler (passenger) dropdown masaustunde design-system Popover + react-popper ile aciliyor.
      Popover'a sadece [arrow, offset] modifier'i veriliyor, popper'in flip modifier'i default acik
      kaliyor ve clippingParents'a gore yer yoksa panel yukari doner.
      FlightPersonDropdown popper ayarlarini disari acmadigi icin flip'i JS'ten kapatamiyoruz;
      flip gerceklestiginde (data-popper-placement="top-*") panel'i tekrar input'un altina aliyoruz.
      Sadece up('md'): mobilde zaten Dialog (bottom-slide) render ediliyor, oraya dokunmuyor.
      Datepicker zaten asagi acildigi icin bu kural ona hic uygulanmaz.
    */
    ${({ isWingie }) =>
      isWingie &&
      css`
        div:has(> [data-popper-placement^='top']) {
          position: relative;
        }

        [data-popper-placement^='top'] {
          inset: auto !important;
          top: 100% !important;
          left: auto !important;
          right: 0 !important;
          transform: none !important;
          margin-top: 10px !important;
        }
      `}
  }

  /* Otel tab yokken Tab.separated-panels kart stilini wrapper'da taklit et */
  ${({ $flightOnly }) =>
    $flightOnly &&
    css`
      background-color: #fff;
      box-shadow: 0px 5px 22px 4px rgba(0, 0, 0, 0.3);
      border-radius: 8px;

      ${down('md')} {
        background: var(--sky-extra-extra-light, #f8faf9);
        box-shadow: none;
        padding: 14px 16px;
      }
    `}

  /*
    Sticky: master shadow/padding (data-state) + Tab mount kalır (form remount olmasın).
    separated-panels kart chrome'u sticky'de nötrlenir; aksi halde ağır gölge sticky UI'yi bozar.
  */
  [data-state='fixed'] && {
    background-color: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    animation: ${updownAnimation} 0.3s ease-in-out;
    animation-fill-mode: both;

    ${up('md')} {
      [role='tablist'] {
        display: none !important;
      }

      [class*='separated-panels'] {
        background-color: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
        gap: 0 !important;
        border-radius: 0 !important;
      }
    }
  }

  ${up('md')} {
    ${(p) =>
      p.market !== 'tr' &&
      `
      [data-testid*="-error-message-wrapper"] {
        position: relative !important;
        padding-bottom: 0px !important;
      }
    `}
  }

  /* Location cards: compact line-height so label/city/airport fit in fixed 100px card
     (body RTL 1.7 would otherwise push the airport line into the clip edge) */
  [data-testid$='-homepage-flight-originInput'],
  [data-testid$='-homepage-flight-destinationInput'] {
    line-height: 1.2;
  }

  [data-testid$='-homepage-flight-originInput'] > div,
  [data-testid$='-homepage-flight-destinationInput'] > div {
    line-height: 1.2 !important;
  }

  [data-testid$='-homepage-flight-originInput'] > div:nth-child(2),
  [data-testid$='-homepage-flight-destinationInput'] > div:nth-child(2) {
    line-height: 1.5 !important;
  }

  /* Desktop RTL passenger input: truncate with …. Mobil butona uygulanmaz — next gibi 2 satıra iner. */
  [dir='rtl'] & [data-testid$='-flight-passenger-input'] {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${down('md')} {
    /* Inline badge sonrası floating NewBadge yok; scroll'da metin/badge taşmasın diye clip */
    [data-testid='endesign-wingie-search-form-tab-list'] {
      width: 100%;
      max-width: 100%;
      overflow: hidden !important;
    }

    [data-testid='endesign-wingie-search-form-tab-left-wrapper'] {
      width: 100%;
      max-width: 100%;
      margin-top: -20px;
      padding-top: 20px;
      overflow-x: auto;
      overflow-y: hidden;
    }

    [data-testid='endesign-wingie-search-form-tab-left-wrapper'] > [role='tab'] {
      position: relative;
      overflow: hidden;
      flex: 1 1 ${({ $tabCount }) => 100 / ($tabCount && $tabCount > 0 ? $tabCount : 2)}%;
      min-width: 0;
      justify-content: center;
      min-height: 48px;
      /* 3 sekmede 16px yatay padding metni kırpıyor */
      padding: ${({ $tabCount }) => (($tabCount || 2) > 2 ? '12px 8px' : '12px 16px')} !important;
    }

    [data-testid='endesign-wingie-search-form-tab-left-wrapper'] > [role='tab']:first-child {
      padding: ${({ $tabCount }) => (($tabCount || 2) > 2 ? '12px 8px' : '12px 16px')} !important;
    }

    [data-testid='endesign-wingie-search-form-tab-left-wrapper'] > [role='tab'] > div {
      justify-content: center;
      width: 100%;
      min-width: 0;
      overflow: hidden;
    }

    [data-testid='endesign-wingie-search-form-tab-left-wrapper'] > [role='tab'] > div > span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ${({ $variant }) =>
      $variant === 'B' &&
      css`
        padding-right: 16px;
        padding-left: 16px;
        padding-top: 8px;
      `}
  }
`
